import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Field, FieldError, FieldGroup, FieldLabel } from '~/shadcn/ui/field'
import {
  Form,
  FormDescription,
  FormField,
  FormItem,
} from '~/shadcn/ui/form/form'
import { Input } from '~/shadcn/ui/input'
import { cn } from '~/shadcn/lib/utils'
import { Spinner } from '~/shadcn/ui/spinner'
import {
  resetPasswordSchema,
  type ResetPasswordSchema,
} from './resetPasswordSchema'
import { FormError } from '~/@mid_ui-form/form-error/FormError'
import { Link } from 'react-router-dom'
import { Button } from '~/shadcn/ui/button/button'
import { HeadingTwo } from '~/@mid_ui/typography/HeadingTwo'

type FormModel = ResetPasswordSchema

type Props = {
  isRequesting: boolean
  onSubmit: (values: FormModel) => void
  error: string | null
}

function ResetPasswordForm({ isRequesting, onSubmit, error }: Props) {
  const form = useForm<FormModel>({
    defaultValues: { password: '', confirmPassword: '' },
    resolver: zodResolver(resetPasswordSchema),
  })

  return (
    <div className="px-5 pt-10 pb-20">
      <Field className="flex flex-col items-start">
        <HeadingTwo className="font-bold mb-10">
          Nastavenie nového hesla
        </HeadingTwo>
      </Field>

      <Field className="flex flex-col items-start gap-15 mb-10">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FieldGroup>
              <FormField
                control={form.control}
                name="password"
                render={({ fieldState, field }) => (
                  <FormItem className="w-full">
                    <Field>
                      <FieldLabel htmlFor="password">Heslo</FieldLabel>
                      <Input
                        id="password"
                        type="password"
                        autoComplete="new-password"
                        className={cn(
                          'w-full',
                          fieldState.error &&
                            'border-destructive aria-invalid:border-destructive'
                        )}
                        aria-invalid={!!fieldState.error}
                        {...field}
                      />
                    </Field>
                    {fieldState.error ? (
                      <FieldError>{fieldState.error.message}</FieldError>
                    ) : (
                      <FormDescription>
                        Najmenej 8 znakov, veľké a malé písmená a číslo.
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ fieldState, field }) => (
                  <FormItem className="w-full">
                    <Field>
                      <FieldLabel htmlFor="confirmPassword">
                        Kontrola hesla
                      </FieldLabel>
                      <Input
                        id="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        className={cn(
                          'w-full',
                          fieldState.error &&
                            'border-destructive aria-invalid:border-destructive'
                        )}
                        aria-invalid={!!fieldState.error}
                        {...field}
                      />
                    </Field>
                    {fieldState?.error?.message && (
                      <FieldError>{fieldState.error.message}</FieldError>
                    )}
                  </FormItem>
                )}
              />

              {error && <FormError error={error} />}

              <Field>
                <Button
                  variant="accent"
                  type="submit"
                  disabled={
                    isRequesting || !!Object.keys(form.formState.errors).length
                  }
                  className="lg:w-auto"
                  size="lg">
                  {isRequesting ? <Spinner className="size-4" /> : 'Potvrdiť'}
                </Button>
              </Field>
            </FieldGroup>
          </form>
        </Form>
        <div className="inline-flex flex-row items-center justify-center gap-2">
          Späť na prihlásenie?{' '}
          <Link to="/login" className="text-accent">
            Prihlásenie
          </Link>
        </div>
      </Field>
    </div>
  )
}

export { ResetPasswordForm }
